import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_DEFAULTS } from '../../config/companyConfig';

/**
 * Sole runtime writer of `<title>` and `<meta name="description">` (via react-helmet-async).
 * Pages pass route-specific props; omitted values fall back to SEO_DEFAULTS.
 */
export default function PageHelmet({
  title,
  /** When set, used as-is (no `| siteName` suffix). Use for homepage brand-first titles. */
  fullTitle: fullTitleOverride,
  /** Route-specific meta description. Falls back to SEO_DEFAULTS.defaultDescription when omitted. */
  description,
  path,
  noIndex = false,
  jsonLd,
  /** When true, marks the page as ready for flight-SEO hydration (survives in <head> after createRoot). */
  flightSeoReady = false,
}) {
  const fullTitle =
    fullTitleOverride ??
    (title ? `${title} | ${SEO_DEFAULTS.siteName}` : SEO_DEFAULTS.homepageFullTitle);
  const desc = description?.trim() ? description : SEO_DEFAULTS.defaultDescription;
  const canonicalUrl = path ? `${SEO_DEFAULTS.baseUrl}${path}` : undefined;
  const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={robotsContent} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
      <meta property="og:image" content={SEO_DEFAULTS.defaultOgImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SEO_DEFAULTS.twitterSite} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={SEO_DEFAULTS.defaultOgImage} />

      {flightSeoReady && <meta name="flight-seo-ready" content="true" />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
